import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Home, User, Phone, Send, Edit2, Trash2, X, Check, Building2, MapPin, Tag } from 'lucide-react';
import { getChatMessages, sendAdminMessage, editAdminMessage, deleteAdminMessage, getProperties } from '../../lib/apiService';
import PropertyDetail from '../../components/PropertyDetail';

const ChatViewPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Edit states
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Property Modal states
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [loadingProperty, setLoadingProperty] = useState(false);
  
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;
    try {
      const res = await getChatMessages(chatId);
      setChat(res.chat);
      setMessages(res.messages || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load chat');
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const formatTime = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const res = await sendAdminMessage(chatId, newMessage);
      if (res.status) {
        setNewMessage('');
        fetchMessages();
      } else {
        toast.error(res.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const startEditing = (msg) => {
    setEditingMessageId(msg.id);
    setEditingContent(msg.message);
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async () => {
    if (!editingContent.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      const res = await editAdminMessage(editingMessageId, editingContent);
      if (res.status) {
        toast.success('Message updated');
        cancelEditing();
        fetchMessages();
      } else {
        toast.error(res.message || 'Failed to update message');
      }
    } catch (err) {
      toast.error('Error updating message');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      const res = await deleteAdminMessage(msgId);
      if (res.status) {
        toast.success('Message deleted');
        fetchMessages();
      } else {
        toast.error(res.message || 'Failed to delete message');
      }
    } catch (err) {
      toast.error('Error deleting message');
    }
  };

  const handleViewProperty = async () => {
    if (!chat?.property_id) return;
    setLoadingProperty(true);
    try {
      const res = await getProperties({ filters: { id: chat.property_id } });
      if (res.properties && res.properties.length > 0) {
        setPropertyDetails(res.properties[0]);
      } else {
        toast.error('Failed to load property details');
      }
    } catch (err) {
      toast.error('Error loading property');
    } finally {
      setLoadingProperty(false);
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const day = formatDate(msg.created_at);
    if (!acc[day]) acc[day] = [];
    acc[day].push(msg);
    return acc;
  }, {});

  const userId = chat?.user_id;

  return (
    <div className="flex flex-col h-[calc(100vh-theme(spacing.16))] lg:h-screen bg-white">
      <Helmet>
        <title>Chat | JoldiPabo Sales</title>
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h2 className="font-semibold text-slate-900">
            {chat?.user_name || 'User'} ↔ {chat?.owner_name || 'Owner'}
          </h2>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            {chat?.user_phone && <span><Phone className="inline w-3 h-3 mr-1" />{chat.user_phone}</span>}
            {chat?.property_title && (
              <span 
                className="text-[#ee7923] font-medium cursor-pointer hover:underline"
                onClick={handleViewProperty}
              >
                <Home className="inline w-3 h-3 mr-1" />
                {chat.property_title} {chat.property_city ? `(${chat.property_city})` : ''} {chat.property_price ? `· Rs ${Number(chat.property_price).toLocaleString('en-IN')}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full font-medium">
            🛡️ Support Agent
          </span>
        </div>
      </div>

      {/* Participants bar */}
      {chat && (
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-2 flex gap-6 text-xs text-slate-600">
          <span className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-[#ee7923]" />
            <strong>{chat.user_name}</strong> (requester)
          </span>
          <span className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-emerald-500" />
            <strong>{chat.owner_name}</strong> (owner)
          </span>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 bg-gradient-to-b from-slate-50/50 to-white">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 mt-12">
            <p className="font-medium">No messages yet</p>
          </div>
        ) : (
          <>
            {Object.entries(groupedMessages).map(([day, dayMsgs]) => (
              <div key={day}>
                {/* Date separator */}
                <div className="flex items-center gap-2 my-4">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium px-3 py-1 bg-white border border-slate-200 rounded-full">{day}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                {dayMsgs.map(msg => {
                  const isUser = String(msg.sender_id) === String(chat?.user_id);
                  const isOwner = String(msg.sender_id) === String(chat?.owner_id);
                  const isAdmin = !isUser && !isOwner;
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex mb-3 ${isAdmin ? 'justify-end' : (isUser ? 'justify-start' : 'justify-end')}`}
                    >
                      {isUser && (
                        <div className="w-7 h-7 rounded-full bg-[#ee7923] flex items-center justify-center text-white text-xs font-bold mr-2 shrink-0 self-end">
                          {(chat?.user_name || 'U')[0]}
                        </div>
                      )}
                      
                      {isAdmin && (
                        <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold mr-2 shrink-0 self-end">
                          J
                        </div>
                      )}
                      
                      <div
                        className={`group relative max-w-[70%] rounded-2xl px-4 py-2.5 shadow-sm ${
                          isUser
                            ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm'
                            : isAdmin 
                              ? 'bg-indigo-600 text-white rounded-tr-sm'
                              : 'bg-slate-900 text-white rounded-tr-sm'
                        }`}
                      >
                        {/* Admin actions */}
                        <div className={`absolute top-2 ${isUser ? '-right-14' : '-left-14'} flex gap-1`}>
                          <button
                            onClick={() => startEditing(msg)}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 shadow-sm"
                            title="Edit"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 shadow-sm"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>

                        {editingMessageId === msg.id ? (
                          <div className="flex flex-col gap-2 min-w-[200px]">
                            <textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              className={`w-full p-2 text-sm rounded border ${
                                isAdmin || !isUser ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'
                              } focus:outline-none`}
                              rows="2"
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={cancelEditing}
                                className={`p-1 rounded-full ${isAdmin || !isUser ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={handleSaveEdit}
                                disabled={savingEdit}
                                className="p-1 rounded-full bg-indigo-500 text-white hover:bg-indigo-600"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm leading-relaxed">{msg.message}</p>
                            <p className={`text-[10px] mt-1 text-right ${isUser ? 'text-slate-400' : 'text-slate-300'}`}>
                              {formatTime(msg.created_at)}
                              {isAdmin ? <span className="ml-1 opacity-70">· Admin</span> : (msg.sender_name && <span className="ml-1 opacity-70">· {msg.sender_name}</span>)}
                            </p>
                          </>
                        )}
                      </div>
                      
                      {isOwner && (
                        <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold ml-2 shrink-0 self-end">
                          {(chat?.owner_name || 'O')[0]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Bottom Input Area */}
      <div className="bg-white border-t border-slate-200 p-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Send a message as JoldiPabo support..."
            className="flex-1 h-10 px-4 rounded-full border border-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
            disabled={sending || loading}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending || loading}
            className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </form>
      </div>

      {/* Property Detail Side Sheet */}
      {propertyDetails && (
        <PropertyDetail
          property={propertyDetails}
          onClose={() => setPropertyDetails(null)}
        />
      )}
    </div>
  );
};

export default ChatViewPage;
